import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plane, ChevronLeft, ChevronRight, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { SEOHead } from "@/components/SEOHead";

interface SingleSelectOption {
  label: string;
  subtext?: string;
}

interface Step {
  question: string;
  subtitle: string;
  type: "single" | "multi" | "text";
  field: string;
  options?: SingleSelectOption[];
  required?: boolean;
  minLength?: number;
}

const steps: Step[] = [
  {
    question: "How often do you fly?",
    subtitle: "This helps us understand who we're building for.",
    type: "single",
    field: "q1_flight_frequency",
    options: [
      { label: "1–2 times a year", subtext: "Occasional traveler" },
      { label: "3–6 times a year", subtext: "Regular traveler" },
      { label: "Monthly or more", subtext: "Frequent flyer" },
      { label: "Weekly", subtext: "Road warrior" },
    ],
  },
  {
    question: "Have you ever missed a flight or cut it dangerously close at security?",
    subtitle: "Be honest — this is anonymous.",
    type: "single",
    field: "q2_missed_flight",
    options: [
      { label: "Yes, I've actually missed a flight" },
      { label: "Yes, I've had some very close calls" },
      { label: "No, but I stress about it every time" },
      { label: "No, I always leave plenty of buffer" },
    ],
  },
  {
    question: "Which features do you find most valuable?",
    subtitle: "Select all that apply.",
    type: "multi",
    field: "q3_valuable_features",
    options: [
      { label: "TSA wait times" },
      { label: "Flight status & tracking" },
      { label: "Leave-by calculator (when should I leave?)" },
      { label: "Push notifications for TSA spikes" },
      { label: "Airport restaurants & amenities" },
      { label: "Saved / favorite airports" },
      { label: "Auto-detect my airport by location" },
    ],
  },
  {
    question: "What's the one thing AirportBuddy is missing that you'd most want?",
    subtitle: "Free text — be as specific as you want. This is the most valuable question.",
    type: "text",
    field: "q4_missing_feature",
    required: true,
    minLength: 3,
  },
  {
    question: "A Pro version is coming with push alerts, a leave-by calculator, and unlimited saved airports. What would feel fair to pay?",
    subtitle: "Pick the option that feels right — not what you think we want to hear.",
    type: "single",
    field: "q5_pricing",
    options: [
      { label: "Nothing — I'd only use the free version" },
      { label: "$2.99 / month", subtext: "~$36/yr" },
      { label: "$4.99 / month", subtext: "~$60/yr" },
      { label: "$24.99 / year", subtext: "Best value" },
      { label: "$34.99 / year", subtext: "~$2.92/mo" },
      { label: "$59.99 one-time lifetime", subtext: "Never pay again" },
      { label: "$79.99 one-time lifetime", subtext: "For frequent flyers" },
    ],
  },
  {
    question: "If we offered a founder lifetime deal — Pro forever, one-time price — at App Store launch, would you grab it?",
    subtitle: "This would be offered exclusively to our first 160 users as a thank-you.",
    type: "single",
    field: "q6_lifetime_deal",
    options: [
      { label: "Yes, at $49.99" },
      { label: "Yes, at $69.99" },
      { label: "Yes, at either price" },
      { label: "Maybe — depends what's included" },
      { label: "No, I'd prefer monthly or annual" },
    ],
  },
  {
    question: "Anything else you want us to know?",
    subtitle: "Complaints, compliments, wild ideas — all welcome.",
    type: "text",
    field: "q7_anything_else",
    required: false,
  },
];

const Survey = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / (steps.length + 1)) * 100;

  const isStepValid = useCallback(() => {
    const value = answers[step.field];
    if (step.type === "single") return !!value;
    if (step.type === "multi") return Array.isArray(value) && value.length > 0;
    if (step.type === "text") {
      if (!step.required) return true;
      return typeof value === "string" && value.trim().length >= (step.minLength || 1);
    }
    return false;
  }, [answers, step]);

  const handleSingleSelect = (option: string) => {
    setAnswers((prev) => ({ ...prev, [step.field]: option }));
  };

  const handleMultiSelect = (option: string) => {
    setAnswers((prev) => {
      const current = (prev[step.field] as string[]) || [];
      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [step.field]: updated };
    });
  };

  const handleTextChange = (value: string) => {
    setAnswers((prev) => ({ ...prev, [step.field]: value }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { error } = await supabase.from("survey_responses").insert({
        user_id: user?.id || null,
        email: user?.email || null,
        q1_flight_frequency: answers.q1_flight_frequency as string,
        q2_missed_flight: answers.q2_missed_flight as string,
        q3_valuable_features: answers.q3_valuable_features as string[],
        q4_missing_feature: answers.q4_missing_feature as string,
        q5_pricing: answers.q5_pricing as string,
        q6_lifetime_deal: answers.q6_lifetime_deal as string,
        q7_anything_else: (answers.q7_anything_else as string) || null,
      });

      if (error) throw error;
      setSubmitted(true);
    } catch {
      toast({
        title: "Something went wrong",
        description: "Your response couldn't be saved. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <SEOHead
          title="Thank You | AirportBuddy Survey"
          description="Thank you for completing the AirportBuddy survey."
          canonicalUrl="/survey"
        />
        <div className="w-full max-w-md text-center space-y-6 animate-in fade-in duration-500">
          <div className="inline-flex items-center justify-center p-4 bg-primary/10 rounded-2xl">
            <Check className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            That's everything — thank you.
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed">
            You're helping shape what AirportBuddy becomes. As one of our first
            160 users you'll get early access to Pro and first dibs on the
            founder lifetime deal at launch.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Quick Survey | AirportBuddy"
        description="Help us build a better AirportBuddy — takes 2 minutes."
        canonicalUrl="/survey"
      />

      {/* Progress bar */}
      <div className="w-full h-1 bg-muted">
        <div
          className="h-full bg-primary transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <div className="px-4 pt-6 pb-2 flex items-center gap-3 max-w-lg mx-auto w-full">
        <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-xl">
          <Plane className="h-5 w-5 text-primary" />
        </div>
        <span className="text-sm font-medium text-muted-foreground">
          {currentStep + 1} of {steps.length}
        </span>
      </div>

      {/* Question area */}
      <div className="flex-1 flex flex-col justify-center px-4 pb-32 max-w-lg mx-auto w-full">
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" key={currentStep}>
          <div className="space-y-2">
            <h2 className="text-xl md:text-2xl font-bold text-foreground leading-tight">
              {step.question}
            </h2>
            <p className="text-sm text-muted-foreground">{step.subtitle}</p>
          </div>

          <div className="space-y-3">
            {step.type === "single" &&
              step.options?.map((option) => {
                const selected = answers[step.field] === option.label;
                return (
                  <button
                    key={option.label}
                    onClick={() => handleSingleSelect(option.label)}
                    className={cn(
                      "w-full text-left px-4 py-3.5 rounded-xl border transition-all duration-200",
                      "min-h-[52px] active:scale-[0.98]",
                      selected
                        ? "border-primary bg-primary/8 ring-1 ring-primary/20"
                        : "border-border bg-card hover:border-primary/40 hover:bg-muted/50"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                          selected
                            ? "border-primary bg-primary"
                            : "border-muted-foreground/30"
                        )}
                      >
                        {selected && (
                          <div className="w-2 h-2 rounded-full bg-primary-foreground" />
                        )}
                      </div>
                      <div>
                        <span className={cn("text-sm font-medium", selected ? "text-foreground" : "text-foreground")}>
                          {option.label}
                        </span>
                        {option.subtext && (
                          <span className="block text-xs text-muted-foreground mt-0.5">
                            {option.subtext}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}

            {step.type === "multi" &&
              step.options?.map((option) => {
                const selected = ((answers[step.field] as string[]) || []).includes(option.label);
                return (
                  <button
                    key={option.label}
                    onClick={() => handleMultiSelect(option.label)}
                    className={cn(
                      "w-full text-left px-4 py-3.5 rounded-xl border transition-all duration-200",
                      "min-h-[52px] active:scale-[0.98]",
                      selected
                        ? "border-primary bg-primary/8 ring-1 ring-primary/20"
                        : "border-border bg-card hover:border-primary/40 hover:bg-muted/50"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selected}
                        className="pointer-events-none"
                        aria-hidden
                      />
                      <span className="text-sm font-medium text-foreground">
                        {option.label}
                      </span>
                    </div>
                  </button>
                );
              })}

            {step.type === "text" && (
              <Textarea
                value={(answers[step.field] as string) || ""}
                onChange={(e) => handleTextChange(e.target.value)}
                placeholder={step.required ? "Type your answer here..." : "Optional — skip if you'd like"}
                className="min-h-[140px] text-base"
                autoFocus
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border px-4 py-4">
        <div className="max-w-lg mx-auto flex gap-3">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="min-h-[48px] gap-1.5"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={!isStepValid() || submitting}
            className="flex-1 min-h-[48px] gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : currentStep === steps.length - 1 ? (
              "Submit"
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Survey;
